import { ref, watch, onMounted } from 'vue';
import type { NavigationItem } from '../types/navigation.types';
import { STORAGE_KEY } from '@/shared/lib/navigation/const';

interface NavigationState {
  selectedCategory: string;
  selectedModules: NavigationItem[];
  selectedItemId: string | null;
  expandedItems: string[];
}

export function useNavigationState(shouldLoadState = false) {
  const selectedCategory = ref<string>('');
  const selectedModules = ref<NavigationItem[]>([]);
  const selectedItemId = ref<string | null>(null);
  const expandedItems = ref<Set<string>>(new Set());

  const loadState = () => {
    if (!shouldLoadState) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const state: NavigationState = JSON.parse(stored);
        selectedCategory.value = state.selectedCategory || '';
        selectedModules.value = state.selectedModules || [];
        selectedItemId.value = state.selectedItemId || null;
        expandedItems.value = new Set(state.expandedItems || []);
      }
    } catch (error) {
      console.error('Erro ao carregar estado da navegação:', error);
    }
  };

  const saveState = () => {
    try {
      const state: NavigationState = {
        selectedCategory: selectedCategory.value,
        selectedModules: selectedModules.value,
        selectedItemId: selectedItemId.value,
        expandedItems: Array.from(expandedItems.value),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Erro ao salvar estado da navegação:', error);
    }
  };

  const clearState = () => {
    localStorage.removeItem(STORAGE_KEY);
    selectedCategory.value = '';
    selectedModules.value = [];
    selectedItemId.value = null;
    expandedItems.value.clear();
  };

  const clearAllState = () => {
    selectedCategory.value = '';
    selectedModules.value = [];
    selectedItemId.value = null;
    expandedItems.value.clear();
    localStorage.removeItem(STORAGE_KEY);
  };

  watch(
    [selectedCategory, selectedModules, selectedItemId],
    () => {
      if (shouldLoadState) {
        saveState();
      }
    },
    { deep: true }
  );

  watch(
    expandedItems,
    () => {
      if (shouldLoadState) {
        saveState();
      }
    },
    { deep: true }
  );

  onMounted(() => {
    loadState();
  });

  return {
    selectedCategory,
    selectedModules,
    selectedItemId,
    expandedItems,
    loadState,
    saveState,
    clearState,
    clearAllState,
  };
}
